import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Settings, Phone, Mail, CreditCard, Check, X, 
  Eye, EyeOff, RefreshCw, Send, AlertCircle, 
  CheckCircle, Loader2, Globe, MapPin
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Panel de Configuración de Integraciones para Admin
 * Permite configurar Twilio (SMS/WhatsApp) y Brevo (Email)
 */
export const IntegrationsConfigPanel = ({ adminId }) => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language?.startsWith('es');
  
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  
  // Twilio state
  const [twilioConfig, setTwilioConfig] = useState({
    account_sid: '',
    auth_token: '',
    phone_number: '',
    whatsapp_number: '',
    verify_service_sid: ''
  });
  const [showTwilioToken, setShowTwilioToken] = useState(false);
  const [savingTwilio, setSavingTwilio] = useState(false);
  const [testingTwilio, setTestingTwilio] = useState(false);
  
  // Local Numbers state (para SMS bidireccional por país)
  const [localNumbers, setLocalNumbers] = useState({
    ES: '',  // España
    FR: '',  // Francia
    UK: '',  // Reino Unido
    US: ''   // Estados Unidos
  });
  const [savingLocalNumbers, setSavingLocalNumbers] = useState(false);
  
  // Brevo state
  const [brevoConfig, setBrevoConfig] = useState({
    api_key: '',
    sender_email: 'hola@lokalgrow.com',
    sender_name: 'LokAlGrow'
  });
  const [showBrevoKey, setShowBrevoKey] = useState(false);
  const [savingBrevo, setSavingBrevo] = useState(false);
  const [testingBrevo, setTestingBrevo] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const content = {
    es: {
      title: "Configuración de Integraciones",
      subtitle: "Configura las credenciales para SMS, WhatsApp y Email",
      twilio: {
        title: "Twilio (SMS & WhatsApp)",
        description: "Configura Twilio para enviar mensajes SMS y WhatsApp a tus clientes",
        accountSid: "Account SID",
        accountSidHelp: "Empieza con 'AC'. Encuéntralo en tu consola de Twilio",
        authToken: "Auth Token",
        authTokenHelp: "Token secreto de autenticación",
        phoneNumber: "Número de Teléfono SMS",
        phoneNumberHelp: "Formato: +34612345678",
        whatsappNumber: "Número de WhatsApp Business",
        whatsappNumberHelp: "Formato: +14155238886 (Sandbox) o tu número verificado",
        verifyServiceSid: "Verify Service SID (opcional)",
        verifyServiceSidHelp: "Para verificación de teléfonos",
        save: "Guardar Configuración",
        test: "Probar Conexión",
        configured: "Configurado",
        notConfigured: "No configurado"
      },
      localNumbers: {
        title: "Números Locales por País",
        description: "Sara usará automáticamente el número local del país del destinatario para mejor entrega y respuestas bidireccionales",
        spain: "España (+34)",
        france: "Francia (+33)",
        uk: "Reino Unido (+44)",
        us: "Estados Unidos (+1)",
        placeholder: "+34XXXXXXXXX",
        save: "Guardar Números Locales",
        webhookNote: "Recuerda configurar el webhook en cada número de Twilio:",
        webhookUrl: "URL del Webhook"
      },
      brevo: {
        title: "Brevo (Email)",
        description: "Configura Brevo para enviar emails transaccionales y de marketing",
        apiKey: "API Key",
        apiKeyHelp: "Encuéntrala en tu cuenta de Brevo → SMTP & API",
        senderEmail: "Email del Remitente",
        senderEmailHelp: "Debe estar verificado en Brevo",
        senderName: "Nombre del Remitente",
        save: "Guardar Configuración",
        test: "Probar Conexión",
        testEmail: "Enviar Email de Prueba",
        testEmailTo: "Email de destino",
        configured: "Configurado",
        notConfigured: "No configurado"
      },
      status: {
        success: "Configuración guardada correctamente",
        testSuccess: "Conexión exitosa",
        testFailed: "Error de conexión",
        emailSent: "Email de prueba enviado"
      }
    },
    en: {
      title: "Integrations Configuration",
      subtitle: "Configure credentials for SMS, WhatsApp and Email",
      twilio: {
        title: "Twilio (SMS & WhatsApp)",
        description: "Configure Twilio to send SMS and WhatsApp messages to your customers",
        accountSid: "Account SID",
        accountSidHelp: "Starts with 'AC'. Find it in your Twilio console",
        authToken: "Auth Token",
        authTokenHelp: "Secret authentication token",
        phoneNumber: "SMS Phone Number",
        phoneNumberHelp: "Format: +34612345678",
        whatsappNumber: "WhatsApp Business Number",
        whatsappNumberHelp: "Format: +14155238886 (Sandbox) or your verified number",
        verifyServiceSid: "Verify Service SID (optional)",
        verifyServiceSidHelp: "For phone verification",
        save: "Save Configuration",
        test: "Test Connection",
        configured: "Configured",
        notConfigured: "Not configured"
      },
      localNumbers: {
        title: "Local Numbers by Country",
        description: "Sara will automatically use the local number for the recipient's country for better delivery and bidirectional responses",
        spain: "Spain (+34)",
        france: "France (+33)",
        uk: "United Kingdom (+44)",
        us: "United States (+1)",
        placeholder: "+34XXXXXXXXX",
        save: "Save Local Numbers",
        webhookNote: "Remember to configure the webhook on each Twilio number:",
        webhookUrl: "Webhook URL"
      },
      brevo: {
        title: "Brevo (Email)",
        description: "Configure Brevo to send transactional and marketing emails",
        apiKey: "API Key",
        apiKeyHelp: "Find it in your Brevo account → SMTP & API",
        senderEmail: "Sender Email",
        senderEmailHelp: "Must be verified in Brevo",
        senderName: "Sender Name",
        save: "Save Configuration",
        test: "Test Connection",
        testEmail: "Send Test Email",
        testEmailTo: "Destination email",
        configured: "Configured",
        notConfigured: "Not configured"
      },
      status: {
        success: "Configuration saved successfully",
        testSuccess: "Connection successful",
        testFailed: "Connection error",
        emailSent: "Test email sent"
      }
    }
  };

  const c = isSpanish ? content.es : content.en;

  useEffect(() => {
    fetchStatus();
  }, [adminId]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/integrations/status`, {
        headers: { 'X-Admin-Id': adminId }
      });
      setStatus(data);
      
      // Cargar configuraciones existentes
      await fetchTwilioConfig();
      await fetchBrevoConfig();
    } catch (err) {
      console.error('Error fetching status:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTwilioConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/integrations/twilio`, {
        headers: { 'X-Admin-Id': adminId }
      });
      if (data.configured) {
        setTwilioConfig(prev => ({
          ...prev,
          account_sid: data.account_sid || '',
          phone_number: data.phone_number || '',
          whatsapp_number: data.whatsapp_number || '',
          verify_service_sid: data.verify_service_sid || ''
        }));
        // Cargar números locales
        if (data.local_numbers) {
          setLocalNumbers({
            ES: data.local_numbers.ES || '',
            FR: data.local_numbers.FR || '',
            UK: data.local_numbers.UK || '',
            US: data.local_numbers.US || ''
          });
        }
      }
    } catch (err) {
      console.error('Error fetching Twilio config:', err);
    }
  };

  const fetchBrevoConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/integrations/brevo`, {
        headers: { 'X-Admin-Id': adminId }
      });
      if (data.configured) {
        setBrevoConfig(prev => ({
          ...prev,
          sender_email: data.sender_email || 'hola@lokalgrow.com',
          sender_name: data.sender_name || 'LokAlGrow'
        }));
      }
    } catch (err) {
      console.error('Error fetching Brevo config:', err);
    }
  };

  const saveTwilioConfig = async () => {
    if (!twilioConfig.account_sid || !twilioConfig.auth_token) {
      toast.error('Account SID y Auth Token son requeridos');
      return;
    }
    
    try {
      setSavingTwilio(true);
      await axios.post(`${API}/admin/integrations/twilio`, twilioConfig, {
        headers: { 'X-Admin-Id': adminId }
      });
      
      toast.success(c.status.success);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving configuration');
    } finally {
      setSavingTwilio(false);
    }
  };

  const testTwilioConnection = async () => {
    try {
      setTestingTwilio(true);
      const { data } = await axios.post(`${API}/admin/integrations/twilio/test`, null, {
        headers: { 'X-Admin-Id': adminId }
      });
      
      if (data.success) {
        toast.success(`${c.status.testSuccess}: ${data.account_name}`);
      } else {
        toast.error(`${c.status.testFailed}: ${data.error}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingTwilio(false);
    }
  };

  const saveLocalNumbers = async () => {
    try {
      setSavingLocalNumbers(true);
      await axios.post(`${API}/admin/integrations/twilio/local-numbers`, localNumbers, {
        headers: { 'X-Admin-Id': adminId }
      });
      
      toast.success(c.status.success);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving local numbers');
    } finally {
      setSavingLocalNumbers(false);
    }
  };

  const saveBrevoConfig = async () => {
    if (!brevoConfig.api_key) {
      toast.error('API Key es requerida');
      return;
    }
    
    try {
      setSavingBrevo(true);
      await axios.post(`${API}/admin/integrations/brevo`, brevoConfig, {
        headers: { 'X-Admin-Id': adminId }
      });
      
      toast.success(c.status.success);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving configuration');
    } finally {
      setSavingBrevo(false);
    }
  };

  const testBrevoConnection = async () => {
    try {
      setTestingBrevo(true);
      const { data } = await axios.post(`${API}/admin/integrations/brevo/test`, null, {
        headers: { 'X-Admin-Id': adminId }
      });
      
      if (data.success) {
        toast.success(`${c.status.testSuccess}: ${data.company_name}`);
      } else {
        toast.error(`${c.status.testFailed}: ${data.error}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingBrevo(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Ingresa un email de destino');
      return;
    }
    
    try {
      const { data } = await axios.post(
        `${API}/admin/integrations/brevo/test-email?to_email=${encodeURIComponent(testEmail)}`,
        null,
        { headers: { 'X-Admin-Id': adminId } }
      );
      
      if (data.success) {
        toast.success(c.status.emailSent);
        setTestEmail('');
      } else {
        toast.error(data.error || 'Error sending email');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error sending email');
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-400">
        <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
        Loading...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="integrations-config-panel">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Settings className="w-6 h-6" />
          {c.title}
        </h2>
        <p className="text-gray-400 text-sm mt-1">{c.subtitle}</p>
      </div>

      {/* Status Summary */}
      {status && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className={`p-4 rounded-xl ${status.twilio?.configured ? 'bg-green-900/20 border border-green-800' : 'bg-gray-800 border border-gray-700'}`}>
            <div className="flex items-center gap-2 mb-1">
              <Phone className="w-4 h-4" />
              <span className="font-medium text-white">Twilio SMS</span>
            </div>
            <div className="flex items-center gap-1 text-sm">
              {status.twilio?.configured ? (
                <><CheckCircle className="w-4 h-4 text-green-400" /><span className="text-green-400">{c.twilio.configured}</span></>
              ) : (
                <><AlertCircle className="w-4 h-4 text-gray-400" /><span className="text-gray-400">{c.twilio.notConfigured}</span></>
              )}
            </div>
          </div>
          
          <div className={`p-4 rounded-xl ${status.twilio?.has_whatsapp ? 'bg-green-900/20 border border-green-800' : 'bg-gray-800 border border-gray-700'}`}>
            <div className="flex items-center gap-2 mb-1">
              <Phone className="w-4 h-4" />
              <span className="font-medium text-white">WhatsApp</span>
            </div>
            <div className="flex items-center gap-1 text-sm">
              {status.twilio?.has_whatsapp ? (
                <><CheckCircle className="w-4 h-4 text-green-400" /><span className="text-green-400">{c.twilio.configured}</span></>
              ) : (
                <><AlertCircle className="w-4 h-4 text-gray-400" /><span className="text-gray-400">{c.twilio.notConfigured}</span></>
              )}
            </div>
          </div>
          
          <div className={`p-4 rounded-xl ${status.brevo?.configured ? 'bg-green-900/20 border border-green-800' : 'bg-gray-800 border border-gray-700'}`}>
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4" />
              <span className="font-medium text-white">Brevo Email</span>
            </div>
            <div className="flex items-center gap-1 text-sm">
              {status.brevo?.configured ? (
                <><CheckCircle className="w-4 h-4 text-green-400" /><span className="text-green-400">{c.brevo.configured}</span></>
              ) : (
                <><AlertCircle className="w-4 h-4 text-gray-400" /><span className="text-gray-400">{c.brevo.notConfigured}</span></>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Twilio Configuration */}
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-900/30 flex items-center justify-center">
            <Phone className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">{c.twilio.title}</h3>
            <p className="text-sm text-gray-400">{c.twilio.description}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{c.twilio.accountSid}</label>
            <input
              type="text"
              value={twilioConfig.account_sid}
              onChange={(e) => setTwilioConfig({...twilioConfig, account_sid: e.target.value})}
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              data-testid="twilio-account-sid"
            />
            <p className="text-xs text-gray-500 mt-1">{c.twilio.accountSidHelp}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{c.twilio.authToken}</label>
            <div className="relative">
              <input
                type={showTwilioToken ? "text" : "password"}
                value={twilioConfig.auth_token}
                onChange={(e) => setTwilioConfig({...twilioConfig, auth_token: e.target.value})}
                placeholder="••••••••••••••••"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white pr-10"
                data-testid="twilio-auth-token"
              />
              <button 
                type="button"
                onClick={() => setShowTwilioToken(!showTwilioToken)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showTwilioToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">{c.twilio.authTokenHelp}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{c.twilio.phoneNumber}</label>
            <input
              type="text"
              value={twilioConfig.phone_number}
              onChange={(e) => setTwilioConfig({...twilioConfig, phone_number: e.target.value})}
              placeholder="+34612345678"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              data-testid="twilio-phone"
            />
            <p className="text-xs text-gray-500 mt-1">{c.twilio.phoneNumberHelp}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{c.twilio.whatsappNumber}</label>
            <input
              type="text"
              value={twilioConfig.whatsapp_number}
              onChange={(e) => setTwilioConfig({...twilioConfig, whatsapp_number: e.target.value})}
              placeholder="+14155238886"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              data-testid="twilio-whatsapp"
            />
            <p className="text-xs text-gray-500 mt-1">{c.twilio.whatsappNumberHelp}</p>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-1">{c.twilio.verifyServiceSid}</label>
            <input
              type="text"
              value={twilioConfig.verify_service_sid}
              onChange={(e) => setTwilioConfig({...twilioConfig, verify_service_sid: e.target.value})}
              placeholder="VAxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              data-testid="twilio-verify"
            />
            <p className="text-xs text-gray-500 mt-1">{c.twilio.verifyServiceSidHelp}</p>
          </div>
        </div>
        
        <div className="flex gap-3 mt-4">
          <Button 
            onClick={saveTwilioConfig}
            disabled={savingTwilio}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-twilio-btn"
          >
            {savingTwilio ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            {c.twilio.save}
          </Button>
          
          <Button 
            onClick={testTwilioConnection}
            disabled={testingTwilio || !status?.twilio?.configured}
            variant="outline"
          >
            {testingTwilio ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {c.twilio.test}
          </Button>
        </div>
      </div>

      {/* Local Numbers by Country */}
      {status?.twilio?.configured && (
        <div className="bg-gray-800 rounded-xl p-6" data-testid="local-numbers-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-900/30 flex items-center justify-center">
              <Globe className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{c.localNumbers.title}</h3>
              <p className="text-sm text-gray-400">{c.localNumbers.description}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-2">
                <span className="text-lg">🇪🇸</span> {c.localNumbers.spain}
              </label>
              <input
                type="text"
                value={localNumbers.ES}
                onChange={(e) => setLocalNumbers({...localNumbers, ES: e.target.value})}
                placeholder="+34612345678"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                data-testid="local-number-es"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-2">
                <span className="text-lg">🇫🇷</span> {c.localNumbers.france}
              </label>
              <input
                type="text"
                value={localNumbers.FR}
                onChange={(e) => setLocalNumbers({...localNumbers, FR: e.target.value})}
                placeholder="+33612345678"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                data-testid="local-number-fr"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-2">
                <span className="text-lg">🇬🇧</span> {c.localNumbers.uk}
              </label>
              <input
                type="text"
                value={localNumbers.UK}
                onChange={(e) => setLocalNumbers({...localNumbers, UK: e.target.value})}
                placeholder="+447123456789"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                data-testid="local-number-uk"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-2">
                <span className="text-lg">🇺🇸</span> {c.localNumbers.us}
              </label>
              <input
                type="text"
                value={localNumbers.US}
                onChange={(e) => setLocalNumbers({...localNumbers, US: e.target.value})}
                placeholder="+15705651588"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                data-testid="local-number-us"
              />
            </div>
          </div>
          
          {/* Webhook info */}
          <div className="mt-4 p-3 bg-amber-900/20 border border-amber-800/50 rounded-lg">
            <p className="text-sm text-amber-300 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {c.localNumbers.webhookNote}
            </p>
            <code className="block mt-2 text-xs bg-gray-900 p-2 rounded text-green-400 font-mono">
              https://lokalgrow.com/api/reactivation/webhook/sms
            </code>
          </div>
          
          <div className="flex gap-3 mt-4">
            <Button 
              onClick={saveLocalNumbers}
              disabled={savingLocalNumbers}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="save-local-numbers-btn"
            >
              {savingLocalNumbers ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {c.localNumbers.save}
            </Button>
          </div>
        </div>
      )}

      {/* Brevo Configuration */}
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-blue-900/30 flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">{c.brevo.title}</h3>
            <p className="text-sm text-gray-400">{c.brevo.description}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-1">{c.brevo.apiKey}</label>
            <div className="relative">
              <input
                type={showBrevoKey ? "text" : "password"}
                value={brevoConfig.api_key}
                onChange={(e) => setBrevoConfig({...brevoConfig, api_key: e.target.value})}
                placeholder="xkeysib-xxxxxxxxxxxxxxxxxxxx"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white pr-10"
                data-testid="brevo-api-key"
              />
              <button 
                type="button"
                onClick={() => setShowBrevoKey(!showBrevoKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showBrevoKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">{c.brevo.apiKeyHelp}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{c.brevo.senderEmail}</label>
            <input
              type="email"
              value={brevoConfig.sender_email}
              onChange={(e) => setBrevoConfig({...brevoConfig, sender_email: e.target.value})}
              placeholder="hola@lokalgrow.com"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              data-testid="brevo-sender-email"
            />
            <p className="text-xs text-gray-500 mt-1">{c.brevo.senderEmailHelp}</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{c.brevo.senderName}</label>
            <input
              type="text"
              value={brevoConfig.sender_name}
              onChange={(e) => setBrevoConfig({...brevoConfig, sender_name: e.target.value})}
              placeholder="LokAlGrow"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              data-testid="brevo-sender-name"
            />
          </div>
        </div>
        
        <div className="flex flex-wrap gap-3 mt-4">
          <Button 
            onClick={saveBrevoConfig}
            disabled={savingBrevo}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-brevo-btn"
          >
            {savingBrevo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            {c.brevo.save}
          </Button>
          
          <Button 
            onClick={testBrevoConnection}
            disabled={testingBrevo || !status?.brevo?.configured}
            variant="outline"
          >
            {testingBrevo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {c.brevo.test}
          </Button>
        </div>
        
        {/* Test Email Section */}
        {status?.brevo?.configured && (
          <div className="mt-4 pt-4 border-t border-gray-700">
            <label className="block text-sm font-medium text-gray-300 mb-2">{c.brevo.testEmail}</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder={c.brevo.testEmailTo}
                className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                data-testid="test-email-input"
              />
              <Button 
                onClick={sendTestEmail}
                variant="outline"
                disabled={!testEmail}
              >
                <Send className="w-4 h-4 mr-2" />
                {c.brevo.testEmail}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationsConfigPanel;
